/**
 * Rep activity event log — client-side queue (#1950).
 *
 * Every state-changing UI path calls `emit(...)` exactly once at the
 * moment the action happens. The event lands synchronously in IndexedDB
 * (`event_queue` store) before any network call is attempted, so the
 * record survives an offline window, a tab close, a phone restart.
 *
 * The sync engine (`services/eventSync.ts`) picks up pending records and
 * POSTs them in batches to `/api/projects/events/`. On a 200 ack the
 * server has accepted the event and the queue row is removed; on a 4xx
 * for that individual event we mark it failed and stop retrying; on a
 * network-level failure we leave it pending for the next sweep.
 *
 * Locked-in contract:
 *   - PWA is the SOLE emitter of rep-action events. Server endpoints DO
 *     NOT emit duplicates for events that originated here.
 *   - One `client_event_id` (UUID v4) per logical event. The server's
 *     UNIQUE constraint collapses any retries.
 *   - Append-only on the queue. Corrections are new events with
 *     `correction_of` set.
 */

import { openDatabase, STORES } from './indexedDB';

// ------------------------------------------------------------------
// Closed set of event types. Kept in lockstep with
// apps/projects/event_types.py — the server validates against this exact
// set and rejects unknown values, so adding a new event type requires a
// server + client change in the same PR.
// ------------------------------------------------------------------
export type EventType =
  | 'day_started'
  | 'day_ended'
  | 'drive_started'
  | 'drive_ended'
  | 'store_checked_in'
  | 'store_checked_out'
  | 'store_visit_abandoned'
  | 'task_started'
  | 'task_paused'
  | 'task_resumed'
  | 'task_finished'
  | 'task_abandoned'
  | 'survey_response_saved'
  | 'survey_submitted'
  | 'photo_uploaded'
  | 'document_uploaded'
  | 'order_created'
  | 'inventory_recorded'
  | 'oos_recorded'
  | 'time_edited'
  | 'app_resumed_from_offline';

// ------------------------------------------------------------------
// Public input shape — what callers pass to `emit()`.
// ------------------------------------------------------------------
export interface EmitInput {
  event_type: EventType;
  /** Local clock at the moment of the action. Defaults to `new Date()`. */
  event_at?: Date | string;
  store_id?: number | null;
  route_id?: number | null;
  project_id?: number | null;
  calllog_id?: number | null;
  /** Set when this event corrects/supersedes an earlier event (event id). */
  correction_of?: number | null;
  /**
   * Set when this event supersedes one this device emitted. The device never
   * learns the server's event id, so it names the target by the id it minted
   * and the server resolves it.
   */
  correction_of_client_event_id?: string | null;
  /** Event-type-specific structured data. NO PII plaintext. */
  payload?: Record<string, unknown>;
  /** Optional explicit id. If omitted, a UUID v4 is generated. Callers
   *  should generally let the helper mint the id so retries collapse. */
  client_event_id?: string;
}

// ------------------------------------------------------------------
// Persisted queue record. Local-only fields prefixed with `_` are sync
// engine bookkeeping and never get serialized in the POST payload.
// ------------------------------------------------------------------
export type SyncStatus = 'pending' | 'syncing' | 'error';

export interface EventQueueRecord {
  client_event_id: string;
  event_type: EventType;
  event_at: string; // ISO 8601
  store_id: number | null;
  route_id: number | null;
  project_id: number | null;
  calllog_id: number | null;
  correction_of: number | null;
  correction_of_client_event_id: string | null;
  payload: Record<string, unknown>;
  // Sync engine bookkeeping (not sent to server)
  queued_at: string;
  sync_status: SyncStatus;
  sync_attempts: number;
  last_attempt_at?: string;
  last_error?: string;
}

// Retriable (network / 5xx) failures stay 'pending' and keep retrying — only a
// server 'invalid' response is terminal ('error'). sync_attempts is bumped for
// observability but never parks a row on its own.

// ------------------------------------------------------------------
// crypto.randomUUID is available in every PWA-supporting browser; the
// Math.random fallback only exists to keep the type system happy on the
// off-chance the global is shimmed away in a test harness.
// Same pattern as ManualEntryPreflight idempotency keys (#1867).
// ------------------------------------------------------------------
function generateClientEventId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  // Fallback — not cryptographically strong but uniqueness within a
  // device is sufficient because the server-side UNIQUE constraint is
  // the actual gate.
  const rand = () => Math.random().toString(36).slice(2, 10);
  return `${Date.now().toString(36)}-${rand()}-${rand()}`;
}

function toIsoString(dt: Date | string | undefined): string {
  if (!dt) return new Date().toISOString();
  if (typeof dt === 'string') return dt;
  return dt.toISOString();
}

// ---------------------------------------------------------------------
// Last-known location, shared across all emits so events that don't
// have GPS directly in scope (e.g., task_paused inside useVisitTask)
// can still carry a "rep was approximately here" signal for telemetry.
//
// useVisitDay calls setLastKnownLocation() every time it captures GPS
// (startDay / startDriving / stopDriving / checkIntoStore). emit()
// automatically merges the cached location into payload.lat / .lon /
// .location_age_ms unless the caller passed those explicitly.
//
// Implemented as a tiny module-level cache to avoid adding a React
// context dependency for a value that's intentionally not reactive
// (we don't re-render on location updates).
// ---------------------------------------------------------------------
interface CachedLocation {
  lat: number;
  lon: number;
  capturedAt: number; // performance.now-ish (Date.now)
}
let lastKnownLocation: CachedLocation | null = null;

export function setLastKnownLocation(lat: number | null | undefined, lon: number | null | undefined): void {
  if (lat == null || lon == null) return;
  lastKnownLocation = { lat, lon, capturedAt: Date.now() };
}

export function getLastKnownLocation(): CachedLocation | null {
  return lastKnownLocation;
}

/** Stale-safety cap: location older than this is not merged into emit
 *  payloads. Two hours covers a rep's day window comfortably; older than
 *  that and the cache is almost certainly from a prior session/visit. */
const LOCATION_STALE_MS = 2 * 60 * 60 * 1000;

function mergeLocationIntoPayload(
  payload: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const base = payload ?? {};
  // Caller-supplied values win — only fill gaps.
  if (lastKnownLocation === null) return base;
  const age = Date.now() - lastKnownLocation.capturedAt;
  if (age > LOCATION_STALE_MS) return base;
  const merged: Record<string, unknown> = { ...base };
  if (merged.lat == null) merged.lat = lastKnownLocation.lat;
  if (merged.lon == null) merged.lon = lastKnownLocation.lon;
  if (merged.location_age_ms == null) merged.location_age_ms = age;
  return merged;
}

// ------------------------------------------------------------------
// emit() — synchronously enqueue an event in IDB.
//
// Returns the persisted record so callers can chain (e.g. correction_of
// reference). Errors here are LOGGED, not thrown — a failure to enqueue
// an event must never break the rep's primary action.
// ------------------------------------------------------------------
export async function emit(input: EmitInput): Promise<EventQueueRecord | null> {
  try {
    const record: EventQueueRecord = {
      client_event_id: input.client_event_id ?? generateClientEventId(),
      event_type: input.event_type,
      event_at: toIsoString(input.event_at),
      store_id: input.store_id ?? null,
      route_id: input.route_id ?? null,
      project_id: input.project_id ?? null,
      calllog_id: input.calllog_id ?? null,
      correction_of: input.correction_of ?? null,
      correction_of_client_event_id: input.correction_of_client_event_id ?? null,
      // Auto-merge the last-known rep location into the payload so events
      // without GPS directly in scope still carry a "rep was here"
      // telemetry hint. Caller-supplied lat/lon override the cache.
      payload: mergeLocationIntoPayload(input.payload),
      queued_at: new Date().toISOString(),
      sync_status: 'pending',
      sync_attempts: 0,
    };

    const db = await openDatabase();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORES.EVENT_QUEUE, 'readwrite');
      const store = tx.objectStore(STORES.EVENT_QUEUE);
      // `add` would throw on duplicate client_event_id; `put` silently
      // overwrites. We want neither: a caller passing an explicit
      // client_event_id that already exists is a bug, not a retry path.
      // The retry path is the sync engine deciding to re-send rows that
      // are already in the queue, which doesn't go through emit().
      const req = store.add(record);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });

    // Nudge the sync engine so it can flush this event promptly. The
    // engine debounces internally, so a flurry of emits in the same
    // tick only triggers one drain attempt.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('rdm:event-enqueued'));
    }

    return record;
  } catch (err) {
    // Last-ditch: structured console log so a diagnostic capture can see
    // events that never made it into the queue.
    console.error('[eventLog.emit] failed to enqueue event', { input, err });
    return null;
  }
}

// ------------------------------------------------------------------
// Sync engine accessors — used by services/eventSync.ts. Kept in this
// module so the queue's storage details stay encapsulated.
// ------------------------------------------------------------------

export async function getPendingEvents(limit?: number): Promise<EventQueueRecord[]> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.EVENT_QUEUE, 'readonly');
    const store = tx.objectStore(STORES.EVENT_QUEUE);
    const idx = store.index('sync_status');
    const req = idx.getAll(IDBKeyRange.only('pending'), limit);
    req.onsuccess = () => {
      const rows = (req.result as EventQueueRecord[]) ?? [];
      // Sort by queued_at ascending so older events sync first — preserves
      // the rep's true action order across an offline window.
      rows.sort((a, b) => a.queued_at.localeCompare(b.queued_at));
      resolve(rows);
    };
    req.onerror = () => reject(req.error);
  });
}

export async function getEventQueueDepth(): Promise<number> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.EVENT_QUEUE, 'readonly');
    const store = tx.objectStore(STORES.EVENT_QUEUE);
    const req = store.count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function markEventsSyncing(clientEventIds: string[]): Promise<void> {
  if (clientEventIds.length === 0) return;
  const db = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORES.EVENT_QUEUE, 'readwrite');
    const store = tx.objectStore(STORES.EVENT_QUEUE);
    const now = new Date().toISOString();
    let remaining = clientEventIds.length;
    clientEventIds.forEach((id) => {
      const getReq = store.get(id);
      getReq.onsuccess = () => {
        const row = getReq.result as EventQueueRecord | undefined;
        if (!row) {
          remaining -= 1;
          if (remaining === 0) resolve();
          return;
        }
        row.sync_status = 'syncing';
        row.last_attempt_at = now;
        const putReq = store.put(row);
        putReq.onsuccess = () => {
          remaining -= 1;
          if (remaining === 0) resolve();
        };
        putReq.onerror = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
    });
  });
}

export async function deleteEvents(clientEventIds: string[]): Promise<void> {
  if (clientEventIds.length === 0) return;
  const db = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORES.EVENT_QUEUE, 'readwrite');
    const store = tx.objectStore(STORES.EVENT_QUEUE);
    let remaining = clientEventIds.length;
    clientEventIds.forEach((id) => {
      const req = store.delete(id);
      req.onsuccess = () => {
        remaining -= 1;
        if (remaining === 0) resolve();
      };
      req.onerror = () => reject(req.error);
    });
  });
}

/** Mark events as pending again after a TRANSIENT failure (network, timeout,
 *  5xx / maintenance) so the next sync sweep retries them. Bumps sync_attempts
 *  for observability but keeps the row RETRIABLE ('pending') — a transient
 *  outage must not permanently park a valid event as unreachable 'error'
 *  (the RETRY_THROTTLE gate in eventSync already prevents hot-looping). Only a
 *  genuine server 'invalid' is terminal — see markEventsFailed. */
export async function markEventsRetry(clientEventIds: string[], errorMessage?: string): Promise<void> {
  if (clientEventIds.length === 0) return;
  const db = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORES.EVENT_QUEUE, 'readwrite');
    const store = tx.objectStore(STORES.EVENT_QUEUE);
    let remaining = clientEventIds.length;
    clientEventIds.forEach((id) => {
      const getReq = store.get(id);
      getReq.onsuccess = () => {
        const row = getReq.result as EventQueueRecord | undefined;
        if (!row) {
          remaining -= 1;
          if (remaining === 0) resolve();
          return;
        }
        row.sync_attempts += 1;
        row.last_error = errorMessage;
        row.sync_status = 'pending';
        const putReq = store.put(row);
        putReq.onsuccess = () => {
          remaining -= 1;
          if (remaining === 0) resolve();
        };
        putReq.onerror = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
    });
  });
}

/** Park events as 'error' immediately (e.g., server returned 'invalid'
 *  on the event — retrying won't help). Sync attempts NOT incremented
 *  because this is terminal. */
export async function markEventsFailed(clientEventIds: string[], errorMessage?: string): Promise<void> {
  if (clientEventIds.length === 0) return;
  const db = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORES.EVENT_QUEUE, 'readwrite');
    const store = tx.objectStore(STORES.EVENT_QUEUE);
    let remaining = clientEventIds.length;
    clientEventIds.forEach((id) => {
      const getReq = store.get(id);
      getReq.onsuccess = () => {
        const row = getReq.result as EventQueueRecord | undefined;
        if (!row) {
          remaining -= 1;
          if (remaining === 0) resolve();
          return;
        }
        row.sync_status = 'error';
        row.last_error = errorMessage;
        const putReq = store.put(row);
        putReq.onsuccess = () => {
          remaining -= 1;
          if (remaining === 0) resolve();
        };
        putReq.onerror = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
    });
  });
}

/** Re-queue every parked 'error' event back to 'pending' (attempt counter reset)
 *  so the next drain retries them. Called on an explicit user "Sync Now": legacy
 *  events parked by the old permanent-park behavior — otherwise unreachable, the
 *  drain only selects 'pending' — get a fresh chance. Returns how many were
 *  re-queued. A genuinely 'invalid' event will simply re-park on the next drain. */
export async function requeueErroredEvents(): Promise<number> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.EVENT_QUEUE, 'readwrite');
    const store = tx.objectStore(STORES.EVENT_QUEUE);
    const idx = store.index('sync_status');
    const req = idx.getAll(IDBKeyRange.only('error'));
    req.onsuccess = () => {
      const rows = (req.result as EventQueueRecord[]) ?? [];
      for (const row of rows) {
        row.sync_status = 'pending';
        row.sync_attempts = 0;
        row.last_attempt_at = undefined;
        store.put(row);
      }
      tx.oncomplete = () => resolve(rows.length);
    };
    req.onerror = () => reject(req.error);
  });
}
